/**
 * Precios de Omega Digital — ÚNICA FUENTE DE VERDAD.
 *
 * Todo valor monetario del sitio sale de aquí: tarjetas de tarifa, hero,
 * tabla de /precios, schema JSON-LD, mensajes de WhatsApp y el payload que
 * viaja con cada lead al correo y a Supabase.
 *
 * La cobertura de eventos y bodas se cobra por packs de horas; marca
 * personal y deporte, por bloques de 10 fotos. Las fotos en estudio
 * NO: tienen su propio número, y la razón es el costo. El estudio es
 * arrendado, así que la sesión carga arriendo, traslado y estacionamiento —
 * gastos que existen antes de la primera foto y que no se reparten por hora.
 * Cobrarla a la tarifa horaria de un evento sería vender bajo costo.
 *
 * PARA EDITAR EL PRECIO: cambia el número aquí y en ningún otro lugar.
 *
 * Los valores son enteros en pesos chilenos. Qué se muestra con IVA y qué sin
 * él lo decide `ivaIncluido` en tarifa.js y segmentos.js, no este archivo.
 */

export const IVA = 0.19;

export const PRECIOS = {
  /* Eventos: revelación, boda, graduación y gastronomía comparten tarifa.
     Solo fotografía, sin video. El mínimo es hora y media: una hora en un
     evento no alcanza para contarlo. */
  horaYMedia: 150000,
  dosHoras: 220000,
  /* Pack premium. */
  cuatroHoras: 400000,

  /* Bodas: el pack de 2 horas tiene precio propio. El de 4 horas es el de
     eventos. */
  bodaDosHoras: 180000,

  /* Graduación: por graduado, o por curso cuando contrata el colegio. El
     valor de seis cursos baja a $240.000 por curso (propuesta Primera
     Lectura, dic·2026). */
  graduacionPersona: 75000,
  graduacionCurso: 280000,
  graduacionSeisCursos: 1440000,

  /* Impresión opcional que encarga cada familia después de la entrega. */
  impresion15x21: 3000,
  impresion20x30: 5000,
  impresion30x40Marco: 12000,
  packFamiliaImpreso: 9000,

  /* Emprendedores, marca personal y deporte: una hora, y se cobra por cada
     bloque de 10 fotos editadas. */
  diezFotos: 110000,

  /* Sesión en estudio: precio cerrado, no un múltiplo de la hora. */
  estudio: 75000,
  /* La segunda hora en adelante. Vale menos que la primera y no es un
     descuento: el arriendo y el traslado ya los pagó la sesión, así que la
     hora siguiente solo carga estacionamiento y tiempo. */
  estudioHoraAdicional: 50000,
  /* Cada persona además de la primera, en estudio. */
  estudioPersonaExtra: 10000,

  /* Campaña de temporada (data/campana.js). Precio cerrado y con fecha de
     término: no se compara con la hora ni con la sesión de estudio. */
  navidad: 180000,
};

/**
 * Alcance de la tarifa publicada.
 *
 * Todo precio del sitio es para la Región Metropolitana y no incluye el
 * traslado. Fuera de la RM el traslado cambia el total, así que no hay número
 * publicado: se cotiza.
 *
 * Vive aquí, junto a los precios, y no solo en una FAQ. Una condición que
 * mueve el total se tiene que leer ANTES de que el cliente haga sus cuentas:
 * enterarse después de haberlas hecho se siente como un cargo escondido,
 * aunque esté escrito más abajo en la página.
 *
 * Lo pintan /precios y FotoPacks, que es el único componente que dibuja
 * tarifas — en las páginas de servicio y en los bloques por servicio.
 */
export const ALCANCE = {
  zona: "Región Metropolitana",
  linea: "Precios para la Región Metropolitana. No incluyen traslado. Fuera de la RM, cotizar.",
  corta: "RM, sin traslado. Fuera de la RM, cotizar.",
};

/** Formato chileno: $35.000. Sin decimales, punto como separador de miles. */
export const formatCLP = (valor) =>
  new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(valor);

/** Valor con IVA, redondeado al peso. */
export const conIva = (valor) => Math.round(valor * (1 + IVA));

/**
 * Etiqueta de precio lista para pintar.
 *
 * `ivaIncluido: false` añade "+ IVA" en vez de sumar el impuesto: así lo
 * cotiza el mercado corporativo chileno y así se compara con la competencia.
 */
export const etiquetaPrecio = (valor, { ivaIncluido = true } = {}) =>
  ivaIncluido ? formatCLP(valor) : `${formatCLP(valor)} + IVA`;

/** "$35.000 por hora" — el precio unitario, que es el gancho de búsqueda. */
export const porUnidad = (valor, unidad) => `${formatCLP(valor)} por ${unidad}`;
